/**
 * A register grid you edit **in the table**.
 *
 * Click a cell, it becomes an input where it sits. The row tints, the rest of the table stays
 * exactly where it was, Enter commits and Escape abandons. Nothing opens, nothing replaces
 * the grid, and you never lose your place among two hundred rows.
 *
 * That is the whole point, and it is worth saying why the alternative was wrong. A panel that
 * replaces the grid costs a click to open, a click to close, and — much worse — takes away
 * the surrounding rows at exactly the moment someone is deciding what a value should be. A
 * coordinator correcting a certified amount is looking at the three months above it. Editing
 * is a table operation because the *judgement* is a table operation.
 *
 * Three rules the grid enforces before anything is typed:
 *
 *   - **Only what the approval will accept.** `column.editable` comes from the API, which
 *     reads it from the same `writable()` rule `apply.py` enforces. Offering a field the
 *     approval would refuse is the worst kind of interface: the person fills it in, three
 *     roles approve it, and it fails at the last step for a reason none of them could see.
 *   - **Not a locked row.** During the monthly lock window a register is frozen. Clicking an
 *     editable cell on a locked row does not do nothing — it offers to raise an unlock
 *     request, which is the actual remedy.
 *   - **Not a sealed period.** A closed month is a snapshot. Everything is read-only, and the
 *     grid says so rather than accepting a keystroke it will refuse to send.
 *
 * One grid serves all eight registers, driven by the catalogue the API derives from the
 * models. Eight hand-written grids is how a filter ends up working on risks and silently not
 * on issues.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { ColumnInfo, Envelope, RegisterInfo, RegisterRow } from '@/api/types'
import { navigate } from '@/app/routes'
import { useViewPeriod } from '@/app/viewPeriod'
import { Icon } from '@/components/Icon'
import { Empty, Failed, Skeleton } from '@/components/states'
import { diff, type Change } from '@/lib/diff'
import { formatValue } from '@/lib/format'
import { registerLabel } from '@/lib/registers'
import { GridToolbar } from '@/features/registers/GridToolbar'
import { GridTotals, type Totals } from '@/features/registers/GridTotals'
import { useGridView } from '@/features/registers/useGridView'

const PAGE = 25

/**
 * The value behind a column, wherever it actually lives.
 *
 * A column we ship is a column on the row. A column the tenant defined lives inside
 * `extras`, keyed by the same name. Everything else in this file — the diff, the editor, the
 * chip — is written against one value, and this is the only place that knows there are two
 * places to find it.
 */
function valueOf(row: RegisterRow, column: ColumnInfo): unknown {
  if (column.tenant_field) return (row.extras ?? {})[column.name] ?? null
  return row[column.name]
}


/** A cell being edited: which row, which column, and what has been typed so far. */
type Editing = { rowId: string; column: string; value: string }

export type PendingEdit = {
  register: RegisterInfo
  row: RegisterRow
  column: ColumnInfo
  changes: Change[]
}

export function RegisterGrid({
  register,
  packageId,
  onPropose,
  onUnlock,
}: {
  register: RegisterInfo
  /**
   * The package whose records to show, or `null` for the whole portfolio.
   *
   * Both endpoints already existed — `/packages/{id}/risks` and `/risks` — and only the
   * package-scoped one had a screen. The portfolio one is the question a director actually
   * asks: every risk scored 15 or above, anywhere. The grid's filters and totals are what
   * make 1,291 rows answerable rather than merely present.
   */
  packageId: string | null
  /** Raise an application for what was typed. The grid never writes anything itself. */
  onPropose: (edit: PendingEdit) => void
  /** A locked row was clicked. The remedy is an unlock request, not a disabled cursor. */
  onUnlock: (row: RegisterRow) => void
}) {
  const { t, i18n } = useTranslation()
  const [offset, setOffset] = useState(0)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Editing | null>(null)

  // What this person has done to this register: filters, hidden columns, sort, saved views.
  // Held per viewer rather than on the server — see `useGridView` for why.
  const view = useGridView(register.slug, register.default_sort)
  const sort = view.sort
  const input = useRef<HTMLInputElement | HTMLSelectElement | null>(null)
  const viewPeriod = useViewPeriod((state) => state.code)

  // A sealed month is a snapshot. Nothing in it can change, so nothing in it invites a click.
  const readOnly = viewPeriod !== null
  const label = registerLabel(t, register.slug, register.label).toLowerCase()

  // Built once and shared by the row query, the totals query and the export link, so the
  // three can never disagree about what "the current view" means. An export that quietly
  // ignored a filter would be the worst of the three to get wrong.
  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (search.trim()) params.set('search', search.trim())
    if (viewPeriod) params.set('period', viewPeriod)
    for (const condition of view.params) params.append('filter', condition)
    return params
  }, [search, viewPeriod, view.params])

  const rows = useQuery({
    queryKey: ['register', register.slug, packageId, offset, sort, query.toString()],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams(query)
      params.set('limit', String(PAGE))
      params.set('offset', String(offset))
      params.set('sort', sort)
      return api.get<Envelope<RegisterRow>>(
        packageId
          ? `/packages/${packageId}/${register.slug}?${params}`
          : `/${register.slug}?${params}`,
        signal,
      )
    },
    // The old page stays on screen while the next loads, so paging never flashes an empty
    // table at someone comparing two rows.
    placeholderData: keepPreviousData,
  })

  const totals = useQuery({
    queryKey: ['register-totals', register.slug, packageId, query.toString()],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams(query)
      // Omitted for the portfolio view, where the total is over every package rather than
      // one. `/aggregate` already scopes to the tenant, so an absent package_id is the whole
      // portfolio rather than an unfiltered leak.
      if (packageId) params.set('package_id', packageId)
      return api.get<Totals>(`/${register.slug}/aggregate?${params}`, signal)
    },
    // Supporting detail. It must never hold the rows off the screen.
    placeholderData: keepPreviousData,
  })

  const columns = useMemo(
    () =>
      register.primary
        .map((name) => register.columns.find((column) => column.name === name))
        .filter((column): column is ColumnInfo => column !== undefined)
        .filter((column) => !view.hidden.includes(column.name)),
    [register, view.hidden],
  )

  // Changing register, page, sort or search abandons a half-typed cell. Keeping it would
  // apply the value to whichever row happened to land in that position.
  useEffect(() => {
    setEditing(null)
  }, [register.slug, offset, search, sort, viewPeriod])

  useEffect(() => {
    const element = input.current
    if (!editing || !element) return
    element.focus()
    if (element instanceof HTMLInputElement) element.select()
  }, [editing])

  function open(row: RegisterRow, column: ColumnInfo) {
    if (readOnly) return
    if (row['locked'] === true) {
      onUnlock(row)
      return
    }
    const current = valueOf(row, column)
    setEditing({
      rowId: String(row['id']),
      column: column.name,
      value: current === null || current === undefined ? '' : String(current),
    })
  }

  function commit(row: RegisterRow) {
    if (!editing) return
    const column = columns.find((candidate) => candidate.name === editing.column)
    setEditing(null)
    if (!column) return

    const before = { [column.name]: valueOf(row, column) ?? null }
    const after = { [column.name]: editing.value === '' ? null : editing.value }
    const changes = diff(before, after)
    // Typed into and back out of again is not a change. Raising an application with an empty
    // diff would put three people's attention on nothing.
    if (changes.length === 0) return
    onPropose({ register, row, column, changes })
  }

  if (rows.isError) {
    const error = rows.error
    return (
      <Failed
        message={error instanceof ApiError ? error.friendly : String(error)}
        onRetry={() => void rows.refetch()}
      />
    )
  }

  return (
    <div className="grid" data-testid={`grid-${register.slug}`}>
      <GridToolbar
        columns={register.columns}
        filters={view.filters}
        hidden={view.hidden}
        views={view.views}
        activeView={view.activeView}
        search={search}
        matched={rows.data?.meta.total ?? null}
        exportHref={`/api/v1/${register.slug}/export?package_id=${packageId}&${query}`}
        onSearch={(value) => {
          setSearch(value)
          setOffset(0)
        }}
        onAddFilter={(filter) => {
          view.addFilter(filter)
          setOffset(0)
        }}
        onRemoveFilter={(index) => {
          view.removeFilter(index)
          setOffset(0)
        }}
        onClearFilters={() => {
          view.clearFilters()
          setOffset(0)
        }}
        onToggleColumn={view.toggleColumn}
        onShowAllColumns={view.showAllColumns}
        onSaveView={view.saveView}
        onApplyView={(saved) => {
          view.applyView(saved)
          setOffset(0)
        }}
        onDeleteView={view.deleteView}
      />

      <div className="grid__controls">
        {rows.data ? (
          <p className="grid__count numeric" data-testid="grid-count">
            {t('grid.showing', {
              from: rows.data.meta.total === 0 ? 0 : offset + 1,
              to: offset + rows.data.items.length,
              total: rows.data.meta.total,
            })}
          </p>
        ) : null}
        {/* "Click a cell to edit" over an empty register is an instruction for something
            that is not on the screen. The sealed notice still shows, because *why* you
            cannot edit is worth saying whether or not there is anything to edit. */}
        {readOnly ? (
          <p className="grid__hint grid__hint--sealed">{t('grid.sealed')}</p>
        ) : rows.data && rows.data.items.length > 0 ? (
          <p className="grid__hint">{t('grid.clickToEdit')}</p>
        ) : null}
      </div>

      {rows.isPending ? (
        <Skeleton rows={6} />
      ) : rows.data.items.length === 0 ? (
        <Empty title={search ? t('grid.noMatches', { term: search }) : t('grid.empty', { label })} />
      ) : (
        <div className="grid__scroll" data-scroll="1">
          <table className="table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.name}
                    scope="col"
                    className={column.align === 'end' ? 'table__numeric' : undefined}
                  >
                    {register.sortable.includes(column.name) ? (
                      <button
                        type="button"
                        className="table__sort"
                        onClick={() => {
                          view.setSort(sort === column.name ? `-${column.name}` : column.name)
                          setOffset(0)
                        }}
                        aria-label={t('grid.sortBy', { label: column.label })}
                      >
                        {column.label}
                        <span aria-hidden="true">
                          {sort === column.name ? ' ↑' : sort === `-${column.name}` ? ' ↓' : ''}
                        </span>
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.data.items.map((row) => {
                const locked = row['locked'] === true
                const rowId = String(row['id'])
                return (
                  <tr
                    key={rowId}
                    // The texture, not only the chip. A lock readable down the column at a
                    // glance beats one you have to inspect row by row.
                    data-locked={locked}
                    data-editing={editing?.rowId === rowId ? 'true' : undefined}
                    data-testid="grid-row"
                  >
                    {columns.map((column) => (
                      <Cell
                        key={column.name}
                        row={row}
                        column={column}
                        locked={locked}
                        readOnly={readOnly}
                        editing={
                          editing?.rowId === rowId && editing.column === column.name
                            ? editing
                            : null
                        }
                        inputRef={input}
                        locale={i18n.language}
                        onOpen={() => open(row, column)}
                        onHistory={() =>
                          navigate({ name: 'history', slug: register.slug, rowId })
                        }
                        onChange={(value) =>
                          setEditing((current) => (current ? { ...current, value } : current))
                        }
                        onCommit={() => commit(row)}
                        onCancel={() => setEditing(null)}
                      />
                    ))}
                  </tr>
                )
              })}
            </tbody>
            {/* Computed by the database over every matching row, never over this page. */}
            <GridTotals columns={columns} totals={totals.data} locale={i18n.language} />
          </table>
        </div>
      )}

      {rows.data && rows.data.meta.total > PAGE ? (
        <div className="grid__pager">
          <button
            type="button"
            className="button button--quiet"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            {t('grid.previous')}
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={!rows.data.meta.has_more}
            onClick={() => setOffset(offset + PAGE)}
            data-testid="grid-next"
          >
            {t('grid.next')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function Cell({
  row,
  column,
  locked,
  readOnly,
  editing,
  inputRef,
  locale,
  onOpen,
  onHistory,
  onChange,
  onCommit,
  onCancel,
}: {
  row: RegisterRow
  column: ColumnInfo
  locked: boolean
  readOnly: boolean
  editing: Editing | null
  inputRef: { current: HTMLInputElement | HTMLSelectElement | null }
  locale: string
  onOpen: () => void
  onHistory: () => void
  onChange: (value: string) => void
  onCommit: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const isReference = column.kind === 'reference'
  // A reference column is only editable if we actually know the set. Offering a free-text box
  // for a controlled value invites a member that is not in the set, which the monthly loader
  // would then quarantine.
  const editable = column.editable && !readOnly && (!isReference || column.options.length > 0)

  const className =
    [
      column.align === 'end' ? 'table__numeric numeric' : null,
      column.muted ? 'table__muted' : null,
      editable ? 'table__editable' : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined

  function keys(event: ReactKeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      onCommit()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
  }

  if (editing) {
    return (
      <td className={className} data-column={column.name} data-editing="true">
        {isReference ? (
          <select
            ref={(element) => {
              inputRef.current = element
            }}
            className="cell__input"
            value={editing.value}
            onChange={(event) => onChange(event.target.value)}
            onBlur={onCommit}
            onKeyDown={keys}
            data-testid="cell-input"
          >
            <option value="">—</option>
            {column.options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={(element) => {
              inputRef.current = element
            }}
            className="cell__input"
            type={inputType(column.kind)}
            value={editing.value}
            maxLength={column.max_length ?? undefined}
            onChange={(event) => onChange(event.target.value)}
            // Blur commits rather than discards. Someone who clicked away has finished
            // typing; throwing the value away would be the surprising choice.
            onBlur={onCommit}
            onKeyDown={keys}
            data-testid="cell-input"
          />
        )}
      </td>
    )
  }

  const label = row.labels?.[column.name]
  const raw = valueOf(row, column)
  const text = label ?? formatValue(raw, column.kind, locale, t)

  const body =
    column.name === 'locked' ? (
      locked ? (
        <span className="chip chip--locked">
          <Icon name="lock" size={11} strokeWidth={2} />
          {t('grid.locked')}
        </span>
      ) : (
        <span className="visually-hidden">{t('grid.unlocked')}</span>
      )
    ) : column.tone ? (
      <span className={`chip chip--${toneClass(column.tone, raw, label)}`}>{text}</span>
    ) : (
      text
    )

  const style = column.width ? { maxWidth: `${column.width}px` } : undefined

  if (column.name === 'register_number') {
    // The reference is the row's identity, so it is the thing to click to ask what happened
    // to it. Done here rather than as an extra column because a 29-column grid cannot spare
    // one for a control, and because this is where a reader's eye already is — the
    // reference column is the one pinned in place while the rest scrolls.
    return (
      <td className={className} data-column={column.name} style={style}>
        <button
          type="button"
          className="link"
          onClick={onHistory}
          title={t('history.open')}
          data-testid="row-history-open"
        >
          {body}
        </button>
      </td>
    )
  }

  if (!editable) {
    return (
      <td className={className} data-column={column.name} style={style}>
        {body}
      </td>
    )
  }

  return (
    <td className={className} data-column={column.name} style={style}>
      <button
        type="button"
        className="cell"
        onClick={onOpen}
        title={locked ? t('grid.lockedHint') : t('grid.clickToEdit')}
        data-testid="cell-open"
      >
        {body}
      </button>
    </td>
  )
}

/**
 * Which chip variant a value gets.
 *
 * The API names the *rule* — `payment_status`, `change_status`, `risk_score` — and never the
 * colour, so the palette stays a token swap for the next tenant. The mapping from rule to
 * variant lives here because it is a presentation decision, and the thresholds are the
 * client's own: 16 is the score at which a risk must be reported upward.
 */
function toneClass(tone: string, raw: unknown, label: string | undefined): string {
  if (tone === 'risk_score') {
    const score = Number(raw)
    if (!Number.isFinite(score)) return 'neutral'
    return score >= 16 ? 'bad' : score >= 9 ? 'watch' : 'good'
  }

  const text = (label ?? '').toLowerCase()
  if (!text) return 'neutral'

  if (tone === 'payment_status') {
    if (text.includes('await') || text.includes('submitted')) return 'info'
    if (text.includes('approved') || text.includes('certified') || text.includes('paid')) {
      return 'good'
    }
    return 'watch'
  }

  if (tone === 'change_status') {
    if (text.includes('approved')) return 'good'
    if (text.includes('reject')) return 'bad'
    return 'watch'
  }

  return 'neutral'
}

function inputType(kind: string): string {
  if (kind === 'date') return 'date'
  if (kind === 'integer' || kind === 'number' || kind === 'money' || kind === 'percent') {
    return 'number'
  }
  return 'text'
}
